import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  styled,
  useTheme,
  Avatar,
  Grid,
  useMediaQuery,
  CircularProgress,
  Tooltip,
  Pagination,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  Link,
  Tabs,
  Tab,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/Download";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import SubscriptionIcon from "@mui/icons-material/CardMembership";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import PaletteIcon from "@mui/icons-material/Palette";
import { motion } from "framer-motion";
import { AppDispatch, RootState } from "../../stores";
import {
  getSpecificUserReports,
  ReportResponse,
  BreakEvenCashFlow,
  RevenueCost,
  CO2Savings,
  UtilityDetails,
  EnergyCosts,
} from "../../features/generate-report-module/reportAction";
import { downloadReport, ReportDetails } from "../../utils/downloadReport";
import { getUserById } from "../../features/user-management-module/userAction";
import { getEmbeddedUserReports } from "../../features/estimator-module/estimatorAction";
import JSZip from "jszip";
import { ArrowBack } from "@mui/icons-material";
import { setSelectedUser } from "../../features/user-management-module/userSlice";
import { format } from "date-fns";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  downloadInvoiceAction,
  getBillingHistoryByUserId,
} from "../../features/subscription-module/subscriptionAction";
import { clearSubscriptionMessages } from "../../features/subscription-module/susbscriptionSlice";
import { getSpecificUserFleetReports } from "../../features/fleet-report-module/fleetReportAction";
import {
  downloadFleetReport,
  FleetReportDetails,
} from "../../utils/downloadFleetReport";

// Use ReportResponse for Report to include utilityDetails and energyCosts
type Report = ReportResponse;

interface EstimatorResponse {
  _id: string;
  reportName: string;
  createdAt: string;
  netProfit: number;
  location?: string;
  financialTable?: Array<{ category: string; value: string | number }>;
  breakEvenCashFlow?: BreakEvenCashFlow;
  revenueCost?: RevenueCost;
  co2Savings?: CO2Savings;
  utilityDetails?: UtilityDetails;
  energyCosts?: EnergyCosts;
}

// Interface for embedded report
interface EmbeddedReport {
  firstName: string;
  lastName: string;
  email: string;
  estimator: EstimatorResponse;
}

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
}

const Container = styled(Box)(({ theme }) => ({
  minHeight: "90vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.95)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: "85vh",
  },
}));

const InfoCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f8f8",
  borderRadius: "8px",
  padding: theme.spacing(2),
  border: "1px solid rgba(0, 0, 0, 0.05)",
  marginBottom: theme.spacing(2),
  "&:hover": {
    transition: "background-color 0.2s ease-in-out",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  width: "100%",
  tableLayout: "auto",
  [theme.breakpoints.down("md")]: {
    minWidth: 600,
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: 500,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#4b5b5d",
  fontWeight: 500,
  padding: theme.spacing(0.3),
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: theme.spacing(0.7),
  },
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: "#f5f0e7",
  fontWeight: 600,
  color: "#4b5b5d",
  padding: theme.spacing(1),
  lineHeight: "1rem",
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.85rem",
    padding: theme.spacing(2),
    lineHeight: "0.5rem",
  },
}));

const DownloadButton = styled(IconButton)(({ theme }) => ({
  color: "#4b5b5d",
  "&:hover": {
    backgroundColor: "rgba(75, 91, 93, 0.1)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.5),
  },
}));

const ExportButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  color: "#fff",
  borderRadius: 8,
  padding: theme.spacing(0.9, 2),
  marginLeft: theme.spacing(2),
  marginTop: theme.spacing(1),
  "&:hover": {
    backgroundColor: "#354142",
  },
  "&.Mui-disabled": {
    backgroundColor: "#bdbdbd",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    marginLeft: 0,
  },
}));

const StyledBackButton = styled(IconButton)(({ theme }) => ({
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: "#f2efe9",
  color: "#455354",
  transition: "background 0.24s ease-in-out, color 0.24s ease-in-out",
  "&:hover": {
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    color: "#f2efe9",
  },
}));

const FooterContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  background: "#fff",
  padding: theme.spacing(2),
  zIndex: 1000,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    padding: theme.spacing(1),
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const NoHistoryMessage = styled(Typography)(({ theme }) => ({
  color: "#757575",
  fontSize: "14px",
  fontWeight: 500,
  textAlign: "center",
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: "#d32f2f",
  fontSize: "14px",
  fontWeight: 500,
  textAlign: "center",
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

const LoaderOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 10,
  borderRadius: "8px",
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface TaxBenefitsDetails {
  applyTaxBenefits?: boolean;
  businessTaxRate?: number;
  section179Limit?: number;
  bonusDepreciationPct?: number;
  qualifyingSpend?: number;
  taxSavings?: number;
  upfrontAfterTax?: number;
}

interface ExtendedReportDetails extends ReportDetails {
  taxBenefits?: TaxBenefitsDetails;
  grantCostDetails?: any[];
  excludeIncentives?: boolean;
}

const ViewUser: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const {
    selectedUser,
    loading: userLoading,
    error: userError,
  } = useSelector((state: RootState) => state.users);
  const {
    reports: generatedReports,
    loading: reportsLoading,
    error: reportsError,
    totalPages: generatedTotalPages,
    totalReports: generatedTotalReports,
  } = useSelector((state: RootState) => state.report);
  const {
    reports: embeddedReportsData,
    loading: embeddedReportsLoading,
    error: embeddedReportsError,
  } = useSelector((state: RootState) => state.estimator);
  const {
    billingHistory,
    billingHistoryLoading,
    billingHistoryError,
    invoiceLoading,
    invoiceError,
  } = useSelector((state: RootState) => state.subscription);

  const [embeddedPage, setEmbeddedPage] = useState(1);
  const [embeddedPageSize, setEmbeddedPageSize] = useState(10);
  const [generatedPage, setGeneratedPage] = useState(1);
  const [generatedPageSize, setGeneratedPageSize] = useState(10);
  const [selectedGeneratedReports, setSelectedGeneratedReports] = useState<
    string[]
  >([]);
  const [selectedEmbeddedReports, setSelectedEmbeddedReports] = useState<
    string[]
  >([]);
  const [tabValue, setTabValue] = useState(0);
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<
    string | null
  >(null);

  const embeddedReports: EmbeddedReport[] = embeddedReportsData?.reports || [];
  const embeddedTotalReports = embeddedReportsData?.total || 0;
  const embeddedTotalPages = embeddedReportsData?.pages || 0;

  const [selectedFleetReports, setSelectedFleetReports] = useState<string[]>(
    []
  );
  const [fleetPage, setFleetPage] = useState(1);
  const [fleetPageSize, setFleetPageSize] = useState(10);
  const {
    reports: fleetReports,
    loading: fleetReportsLoading,
    error: fleetReportsError,
    totalReports: fleetTotalReports,
    totalPages: fleetTotalPages,
  } = useSelector((state: RootState) => state.fleetReport);

  useEffect(() => {
    setGeneratedPage(1);
    setGeneratedPageSize(10);
    setEmbeddedPage(1);
    setEmbeddedPageSize(10);
    setSelectedGeneratedReports([]);
    setSelectedEmbeddedReports([]);
    dispatch(setSelectedUser(null));
  }, [userId, dispatch]);

  console.log("Selected User:", selectedUser);

  useEffect(() => {
    if (userId) {
      dispatch(getUserById(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (selectedUser?.id && selectedUser.id === userId) {
      dispatch(
        getSpecificUserReports({
          id: selectedUser.id,
          page: generatedPage,
          limit: generatedPageSize,
        })
      );

      // Add fleet reports fetch
      dispatch(
        getSpecificUserFleetReports({
          id: selectedUser.id,
          page: fleetPage,
          limit: fleetPageSize,
        })
      );

      dispatch(
        getEmbeddedUserReports({
          userId: selectedUser.id,
          page: embeddedPage,
          limit: embeddedPageSize,
        })
      );
      dispatch(getBillingHistoryByUserId(selectedUser.id));
    }
    return () => {
      dispatch(clearSubscriptionMessages());
    };
  }, [
    dispatch,
    selectedUser,
    userId,
    generatedPage,
    generatedPageSize,
    fleetPage,
    fleetPageSize,
    embeddedPage,
    embeddedPageSize,
  ]);

  const truncateDescription = (description: string, maxLength: number = 30) => {
    if (description.length > maxLength) {
      return `${description.slice(0, maxLength)}...`;
    }
    return description;
  };

  const handleDownloadInvoice = async (invoiceId: string, status: string) => {
    if (status.toLowerCase() === "pending") {
      toast.error(
        "The invoice is not available as this is a scheduled plan that has not yet been activated."
      );
      return;
    }
    if (status.toLowerCase() === "canceled") {
      toast.error("The invoice is not available as this plan was canceled.");
      return;
    }

    if (!billingHistoryLoading && !invoiceLoading) {
      setDownloadingInvoiceId(invoiceId);
      try {
        const response = await dispatch(
          downloadInvoiceAction(invoiceId)
        ).unwrap();
        if (response.success && response.data?.pdfUrl) {
          toast.success("Invoice download started");
          const link = document.createElement("a");
          link.href = response.data.pdfUrl;
          link.download = `invoice-${invoiceId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          toast.error(response.message || "Failed to fetch invoice PDF");
        }
      } catch (error) {
        toast.error(invoiceError || "Error downloading invoice");
      } finally {
        setDownloadingInvoiceId(null);
      }
    }
  };

  const isNoBillingHistory =
    billingHistoryError &&
    (billingHistoryError.includes("Subscription not found") ||
      billingHistoryError.includes("No billing history found"));

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleGeneratedPageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setGeneratedPage(value);
  };

  const handleEmbeddedPageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setEmbeddedPage(value);
  };

  const handleGeneratedPageSizeChange = (event: any) => {
    setGeneratedPageSize(event.target.value);
    setGeneratedPage(1);
  };

  const handleEmbeddedPageSizeChange = (event: any) => {
    setEmbeddedPageSize(event.target.value);
    setEmbeddedPage(1);
  };

  const handleSelectAllGenerated = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      const newSelected = generatedReports.map((report) => report._id);
      setSelectedGeneratedReports(newSelected);
    } else {
      setSelectedGeneratedReports([]);
    }
  };

  const handleSelectAllEmbedded = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      const newSelected = embeddedReports.map((report) => report.estimator._id);
      setSelectedEmbeddedReports(newSelected);
    } else {
      setSelectedEmbeddedReports([]);
    }
  };

  const handleSelectGenerated = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    if (event.target.checked) {
      setSelectedGeneratedReports((prev) => [...prev, id]);
    } else {
      setSelectedGeneratedReports((prev) => prev.filter((s) => s !== id));
    }
  };

  const handleSelectEmbedded = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    if (event.target.checked) {
      setSelectedEmbeddedReports((prev) => [...prev, id]);
    } else {
      setSelectedEmbeddedReports((prev) => prev.filter((s) => s !== id));
    }
  };

  const isGeneratedSelected = (id: string) =>
    selectedGeneratedReports.indexOf(id) !== -1;
  const isEmbeddedSelected = (id: string) =>
    selectedEmbeddedReports.indexOf(id) !== -1;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleDownloadPdf = async (
    report: Report | EstimatorResponse,
    userInfo?: UserInfo | null
  ) => {
    if (!report) {
      toast.error("No report data to download.");
      return;
    }

    try {
      setDownloadingPdf(report._id);

      // Extract tax benefits data from the report
      const taxBenefits =
        (report as any).taxBenefits || (report as any).applyTaxBenefits
          ? {
              applyTaxBenefits: (report as any).applyTaxBenefits || false,
              businessTaxRate: (report as any).businessTaxRate || 21,
              section179Limit: (report as any).section179Limit || 0,
              bonusDepreciationPct: (report as any).bonusDepreciationPct || 0,
              qualifyingSpend: (report as any).qualifyingSpend || 0,
              taxSavings: (report as any).taxSavings || 0,
              upfrontAfterTax: (report as any).upfrontAfterTax || 0,
            }
          : undefined;

      const details: ExtendedReportDetails = {
        reportName: report.reportName || "Charging Station",
        netProfit: report.netProfit || 0,
        address:
          (report as EstimatorResponse).location ||
          (report as Report).location ||
          "981 US Route 1, Edison, NJ, USA",
        financialTable: report.financialTable || [],
        breakEvenCashFlow: report.breakEvenCashFlow || {
          labels: ["0", "1", "2", "3", "4", "5"],
          data: Array(6).fill(0),
        },
        revenueCost: report.revenueCost || {
          labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
          datasets: [],
        },
        co2Savings: report.co2Savings || {
          labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
          datasets: [],
        },
        utilityDetails: {
          state: report.utilityDetails?.state || "--",
          zipCode: report.utilityDetails?.zipCode || "--",
          utilityName: report.utilityDetails?.utilityName || "--",
          utilityId: report.utilityDetails?.utilityId || "--",
          scheduleId: report.utilityDetails?.scheduleId || "--",
          totalCost: Number(report.utilityDetails?.totalCost) || 0,
        },
        energyCosts: report.energyCosts || {
          energy: Array(5).fill(0),
          energyTime: Array(5).fill(0),
          incrementalEnergy: Array(5).fill(0),
          demand: Array(5).fill(0),
          demandTime: Array(5).fill(0),
          incrementalDemand: Array(5).fill(0),
          serviceCharge: Array(5).fill(0),
          otherCharges: Array(5).fill(0),
          total: Array(5).fill(0),
        },
        grantCostDetails: (report as any).grantCostDetails || [],
        excludeIncentives: (report as any).excludeIncentives || false,
        taxBenefits: taxBenefits,
        ...(userInfo && {
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.email,
        }),
      };

      await downloadReport(details, false);
      toast.success("Report download started");
    } catch (err) {
      console.error("PDF generation failed", err);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleDownloadMultiplePdfs = async (type: "generated" | "embedded") => {
    const selectedReports =
      type === "generated" ? selectedGeneratedReports : selectedEmbeddedReports;

    if (selectedReports.length === 0) {
      toast.error("Please select at least one report to download.");
      return;
    }

    setDownloadingPdf("multiple");
    try {
      let selectedDataWithInfo: Array<{
        report: Report | EstimatorResponse;
        userInfo?: UserInfo;
      }> = [];

      if (type === "generated") {
        selectedDataWithInfo = generatedReports
          .filter((r) => selectedReports.includes(r._id))
          .map((r) => ({ report: r }));
      } else {
        const selectedFull = embeddedReports.filter((r) =>
          selectedReports.includes(r.estimator._id)
        );
        selectedDataWithInfo = selectedFull.map((r) => ({
          report: r.estimator,
          userInfo: {
            firstName: r.firstName,
            lastName: r.lastName,
            email: r.email,
          },
        }));
      }

      const selectedPromises = selectedDataWithInfo.map(
        async ({ report, userInfo }) => {
          // Extract tax benefits data
          const taxBenefits =
            (report as any).taxBenefits || (report as any).applyTaxBenefits
              ? {
                  applyTaxBenefits: (report as any).applyTaxBenefits || false,
                  businessTaxRate: (report as any).businessTaxRate || 21,
                  section179Limit: (report as any).section179Limit || 0,
                  bonusDepreciationPct:
                    (report as any).bonusDepreciationPct || 0,
                  qualifyingSpend: (report as any).qualifyingSpend || 0,
                  taxSavings: (report as any).taxSavings || 0,
                  upfrontAfterTax: (report as any).upfrontAfterTax || 0,
                }
              : undefined;

          const details: ExtendedReportDetails = {
            reportName: report.reportName || "Charging Station",
            netProfit: report.netProfit || 0,
            address:
              (report as EstimatorResponse).location ||
              (report as Report).location ||
              "981 US Route 1, Edison, NJ, USA",
            financialTable: report.financialTable || [],
            breakEvenCashFlow: report.breakEvenCashFlow || {
              labels: ["0", "1", "2", "3", "4", "5"],
              data: Array(6).fill(0),
            },
            revenueCost: report.revenueCost || {
              labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
              datasets: [],
            },
            co2Savings: report.co2Savings || {
              labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
              datasets: [],
            },
            utilityDetails: {
              state: report.utilityDetails?.state || "--",
              zipCode: report.utilityDetails?.zipCode || "--",
              utilityName: report.utilityDetails?.utilityName || "--",
              utilityId: report.utilityDetails?.utilityId || "--",
              scheduleId: report.utilityDetails?.scheduleId || "--",
              totalCost: Number(report.utilityDetails?.totalCost) || 0,
            },
            energyCosts: report.energyCosts || {
              energy: Array(5).fill(0),
              energyTime: Array(5).fill(0),
              incrementalEnergy: Array(5).fill(0),
              demand: Array(5).fill(0),
              demandTime: Array(5).fill(0),
              incrementalDemand: Array(5).fill(0),
              serviceCharge: Array(5).fill(0),
              otherCharges: Array(5).fill(0),
              total: Array(5).fill(0),
            },
            grantCostDetails: (report as any).grantCostDetails || [],
            excludeIncentives: (report as any).excludeIncentives || false,
            taxBenefits: taxBenefits,
            ...(userInfo && {
              firstName: userInfo.firstName,
              lastName: userInfo.lastName,
              email: userInfo.email,
            }),
          };
          const blob = await downloadReport(details, false, undefined, true);
          return { blob: blob as Blob, report };
        }
      );

      const reportsWithBlobs: {
        blob: Blob;
        report: Report | EstimatorResponse;
      }[] = await Promise.all(selectedPromises);
      const zip = new JSZip();
      reportsWithBlobs.forEach(({ blob, report }) => {
        const safeName = report.reportName.replace(/[^a-z0-9]+/gi, "_");
        const filename = `${safeName}_Report.pdf`;
        zip.file(filename, blob);
      });

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${type === "generated" ? "User" : "Embedded"}_Reports_${
        new Date().toISOString().split("T")[0]
      }.zip`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Multiple reports download started");
    } catch (err) {
      console.error("ZIP generation failed", err);
      toast.error("Failed to generate ZIP file. Please try again.");
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Fleet Estimator handlers
  const handleSelectAllFleet = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = fleetReports.map((report) => report._id);
      setSelectedFleetReports(newSelected);
    } else {
      setSelectedFleetReports([]);
    }
  };

  const handleSelectFleet = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    if (event.target.checked) {
      setSelectedFleetReports((prev) => [...prev, id]);
    } else {
      setSelectedFleetReports((prev) => prev.filter((s) => s !== id));
    }
  };

  const isFleetSelected = (id: string) =>
    selectedFleetReports.indexOf(id) !== -1;

  const handleFleetPageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setFleetPage(value);
  };

  const handleFleetPageSizeChange = (event: any) => {
    setFleetPageSize(event.target.value);
    setFleetPage(1);
  };

  const handleDownloadFleetPdf = async (report: any) => {
    if (!report) {
      toast.error("No fleet report data to download.");
      return;
    }

    try {
      setDownloadingPdf(report._id);

      const details: FleetReportDetails = {
        reportName: report.reportName,
        netSavings: report.netSavings,
        annualSavings: report.annualSavings || 0,
        address: report.location || report.address || "",
        city: report.city || "",
        zipCode: report.zipCode || "",
        fleetSize: report.fleetSize || parseInt(report.numVehicles || "1"),
        analysisYears: report.analysisYears || parseInt(report.years || "7"),
        paybackPeriod: report.paybackPeriod || 0,
        financialTable: report.financialTable || [],
        taxBenefitsTable: report.taxBenefitsTable,
        environmentalTable: report.environmentalTable,
        costOverTimeData: report.costOverTimeData,
        cumulativeSavingsData: report.cumulativeSavingsData,
        environmentalData: report.environmentalData,
        utilityDetails: report.utilityDetails,
        utilityRate: report.utilityRate || 0.14,
        applyTaxBenefits: report.applyTaxBenefits || false,
      };

      await downloadFleetReport(details, false);
      toast.success("Fleet report download started");
    } catch (err) {
      console.error("Fleet PDF generation failed", err);
      toast.error("Failed to generate fleet PDF. Please try again.");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleDownloadMultipleFleetPdfs = async () => {
    if (selectedFleetReports.length === 0) {
      toast.error("Please select at least one fleet report to download.");
      return;
    }

    setDownloadingPdf("multiple");
    try {
      const selectedData = fleetReports.filter((r) =>
        selectedFleetReports.includes(r._id)
      );

      const reportPromises = selectedData.map(async (report) => {
        const details: FleetReportDetails = {
          reportName: report.reportName,
          netSavings: report.netSavings,
          annualSavings: report.annualSavings || 0,
          address: report.location || "",
          city: report.city || "",
          zipCode: report.zipCode || "",
          fleetSize: parseInt(String(report.fleetSize || "1"), 10),
          analysisYears: parseInt(String(report.analysisYears || "7"), 10),
          paybackPeriod: report.paybackPeriod || 0,
          financialTable: report.financialTable || [],
          taxBenefitsTable: report.taxBenefitsTable,
          environmentalTable: report.environmentalTable,
          costOverTimeData: report.costOverTimeData,
          cumulativeSavingsData: report.cumulativeSavingsData,
          environmentalData: report.environmentalData,
          // utilityDetails: report.utilityDetails,
          utilityRate: report.utilityRate || 0.14,
          applyTaxBenefits: report.applyTaxBenefits || false,
        };

        const blob = await downloadFleetReport(details, false, undefined, true);
        return { blob: blob as Blob, report };
      });

      const reportsWithBlobs = await Promise.all(reportPromises);
      const zip = new JSZip();
      reportsWithBlobs.forEach(({ blob, report }) => {
        const safeName = report.reportName.replace(/[^a-z0-9]+/gi, "_");
        const filename = `${safeName}_Fleet_Report.pdf`;
        zip.file(filename, blob);
      });

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Fleet_Reports_${
        new Date().toISOString().split("T")[0]
      }.zip`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Multiple fleet reports download started");
    } catch (err) {
      console.error("Fleet ZIP generation failed", err);
      toast.error("Failed to generate fleet ZIP file. Please try again.");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 },
  };

  const buttonVariants = {
    rest: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 },
  };

  const userData = useMemo(() => selectedUser, [selectedUser]);

  const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box>{children}</Box>}
      </div>
    );
  };

  return (
    <Container>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%",
          maxWidth: "auto",
          margin: "0 auto",
          paddingBottom: "80px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} color="#4b5b5d">
            User Details
          </Typography>
          <Tooltip title="Back">
            <StyledBackButton
              aria-label="back"
              onClick={() => navigate("/main/user-management")}
            >
              <ArrowBack />
            </StyledBackButton>
          </Tooltip>
        </Box>

        {userLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress
              size={25}
              thickness={2}
              sx={{ color: "#455354" }}
            />
          </Box>
        ) : userError ? (
          <Typography
            color="error"
            variant="body1"
            align="center"
            sx={{ py: 3 }}
          >
            {userError}
          </Typography>
        ) : userData ? (
          <motion.div
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <InfoCard>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="#4b5b5d"
                    mb={1.5}
                    display="flex"
                    alignItems="center"
                  >
                    <BadgeIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                    Basic Information
                  </Typography>
                  <motion.div variants={itemVariants}>
                    <Box sx={{ display: "flex", alignItems: "center", py: 1 }}>
                      <Box sx={{ position: "relative" }}>
                        <Avatar
                          src={userData.profileImg || undefined}
                          sx={{
                            width: 60,
                            height: 60,
                            backgroundColor: "#758d8e",
                            fontSize: "1.5rem",
                          }}
                        >
                          {!userData.profileImg &&
                            `${userData.firstname.charAt(
                              0
                            )}${userData.lastname.charAt(0)}`}
                        </Avatar>
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: userData.isActive
                              ? "#388e3c"
                              : "#d32f2f",
                            border: "2px solid #fff",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color="#4b5b5d"
                        ml={2}
                      >
                        {userData.firstname} {userData.lastname}
                      </Typography>
                    </Box>
                  </motion.div>
                  <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.05)" }} />
                  <motion.div variants={itemVariants}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <EmailIcon sx={{ mr: 1, color: "#4b5b5d" }} />
                        <Typography
                          fontWeight={500}
                          color="#4b5b5d"
                          sx={{
                            fontSize: isSmallScreen ? "0.75rem" : "0.75rem",
                          }}
                        >
                          Email Address
                        </Typography>
                      </Box>
                      <Typography
                        fontWeight={600}
                        color="#4b5b5d"
                        sx={{
                          fontSize: isSmallScreen ? "0.75rem" : "0.75rem",
                        }}
                      >
                        {userData.email}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PhoneIcon sx={{ mr: 1, color: "#4b5b5d" }} />
                        <Typography
                          fontWeight={500}
                          color="#4b5b5d"
                          sx={{
                            fontSize: isSmallScreen ? "0.75rem" : "0.75rem",
                          }}
                        >
                          Phone Number
                        </Typography>
                      </Box>
                      <Typography
                        fontWeight={600}
                        color="#4b5b5d"
                        sx={{
                          fontSize: isSmallScreen ? "0.75rem" : "0.75rem",
                        }}
                      >
                        {userData.mobileNo}
                      </Typography>
                    </Box>
                  </motion.div>
                </InfoCard>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <InfoCard>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="#4b5b5d"
                    mb={1.5}
                    display="flex"
                    alignItems="center"
                  >
                    <SubscriptionIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                    Subscription Details
                  </Typography>
                  <motion.div variants={itemVariants}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <SubscriptionIcon sx={{ mr: 1, color: "#4b5b5d" }} />
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          color="#4b5b5d"
                        >
                          Plan
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="#4b5b5d"
                      >
                        {userData.subscription?.planName || "No Subscription"}
                      </Typography>
                    </Box>
                  </motion.div>
                  <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.05)" }} />
                  <motion.div variants={itemVariants}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <DataUsageIcon sx={{ mr: 1, color: "#4b5b5d" }} />
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          color="#4b5b5d"
                        >
                          Site Report Usage
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="#4b5b5d"
                      >
                        {userData.subscription
                          ? `${userData.subscription.siteQuotaUsed ?? 0} / ${
                              userData.subscription.siteQuotaLimit ?? 0
                            }`
                          : "N/A"}
                      </Typography>
                    </Box>
                  </motion.div>
                  {userData.subscription &&
                    userData.subscription.siteQuotaUsed !== undefined &&
                    userData.subscription.siteQuotaLimit !== undefined && (
                      <>
                        <motion.div variants={itemVariants}>
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              color="#4b5b5d"
                            >
                              Usage Progress
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  flexGrow: 1,
                                  height: 8,
                                  backgroundColor: "#f0f0f0",
                                  borderRadius: 4,
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    height: "100%",
                                    width: `${
                                      (userData.subscription.siteQuotaUsed /
                                        userData.subscription.siteQuotaLimit) *
                                      100
                                    }%`,
                                    backgroundColor:
                                      userData.subscription.siteQuotaUsed /
                                        userData.subscription.siteQuotaLimit >
                                      0.8
                                        ? "#f44336"
                                        : "#4caf50",
                                    borderRadius: 4,
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" fontWeight={500}>
                                {Math.round(
                                  (userData.subscription.siteQuotaUsed /
                                    userData.subscription.siteQuotaLimit) *
                                    100
                                )}
                                %
                              </Typography>
                            </Box>
                          </Box>
                        </motion.div>
                      </>
                    )}
                  <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.05)" }} />
                  <motion.div variants={itemVariants}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <DataUsageIcon sx={{ mr: 1, color: "#4b5b5d" }} />
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          color="#4b5b5d"
                        >
                          Fleet Report Usage
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="#4b5b5d"
                      >
                        {userData.subscription
                          ? `${userData.subscription.fleetQuotaUsed ?? 0} / ${
                              userData.subscription.fleetQuotaLimit ?? 0
                            }`
                          : "N/A"}
                      </Typography>
                    </Box>
                  </motion.div>
                  {userData.subscription &&
                    userData.subscription.fleetQuotaUsed !== undefined &&
                    userData.subscription.fleetQuotaLimit !== undefined && (
                      <>
                        <motion.div variants={itemVariants}>
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              color="#4b5b5d"
                            >
                              Usage Progress
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  flexGrow: 1,
                                  height: 8,
                                  backgroundColor: "#f0f0f0",
                                  borderRadius: 4,
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    height: "100%",
                                    width: `${
                                      (userData.subscription.fleetQuotaUsed /
                                        userData.subscription.fleetQuotaLimit) *
                                      100
                                    }%`,
                                    backgroundColor:
                                      userData.subscription.fleetQuotaUsed /
                                        userData.subscription.fleetQuotaLimit >
                                      0.8
                                        ? "#f44336"
                                        : "#4caf50",
                                    borderRadius: 4,
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" fontWeight={500}>
                                {Math.round(
                                  (userData.subscription.fleetQuotaUsed /
                                    userData.subscription.fleetQuotaLimit) *
                                    100
                                )}
                                %
                              </Typography>
                            </Box>
                          </Box>
                        </motion.div>
                      </>
                    )}
                </InfoCard>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <InfoCard>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="#4b5b5d"
                    mb={1.5}
                    display="flex"
                    alignItems="center"
                  >
                    <PaletteIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                    White Label Details
                  </Typography>
                  {!userData.whiteLabel ? (
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="#4b5b5d"
                      align="center"
                    >
                      No White Label Configured
                    </Typography>
                  ) : (
                    <>
                      <motion.div variants={itemVariants}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="#4b5b5d"
                          >
                            Brand Logo
                          </Typography>
                          <img
                            src={userData.whiteLabel.brandLogo}
                            alt="Brand Logo"
                            style={{ maxWidth: 100, height: "auto" }}
                          />
                        </Box>
                      </motion.div>
                      <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.05)" }} />
                      <motion.div variants={itemVariants}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="#4b5b5d"
                          >
                            Brand Color
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                backgroundColor: userData.whiteLabel.brandColor,
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                              }}
                            />
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color="#4b5b5d"
                            >
                              {userData.whiteLabel.brandColor}
                            </Typography>
                          </Box>
                        </Box>
                      </motion.div>
                      <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.05)" }} />
                      <motion.div variants={itemVariants}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="#4b5b5d"
                          >
                            Text Color
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                backgroundColor: userData.whiteLabel.textColor,
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                              }}
                            />
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color="#4b5b5d"
                            >
                              {userData.whiteLabel.textColor}
                            </Typography>
                          </Box>
                        </Box>
                      </motion.div>
                      <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.05)" }} />
                      <motion.div variants={itemVariants}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="#4b5b5d"
                          >
                            Background Color
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                backgroundColor: userData.whiteLabel.bgColor,
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                              }}
                            />
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color="#4b5b5d"
                            >
                              {userData.whiteLabel.bgColor}
                            </Typography>
                          </Box>
                        </Box>
                      </motion.div>
                      <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.05)" }} />
                      <motion.div variants={itemVariants}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="#4b5b5d"
                          >
                            Domain URL
                          </Typography>
                          <Link
                            href={userData.whiteLabel.domainURL}
                            target="_blank"
                            rel="noopener"
                            sx={{ textDecoration: "none", color: "#4b5b5d" }}
                          >
                            {userData.whiteLabel.domainURL}
                          </Link>
                        </Box>
                      </motion.div>
                      <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.05)" }} />
                      <motion.div variants={itemVariants}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="#4b5b5d"
                          >
                            Terms of Service URL
                          </Typography>
                          <Link
                            href={userData.whiteLabel.termsOfServiceURL}
                            target="_blank"
                            rel="noopener"
                            sx={{ textDecoration: "none", color: "#4b5b5d" }}
                          >
                            {userData.whiteLabel.termsOfServiceURL}
                          </Link>
                        </Box>
                      </motion.div>
                      <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.05)" }} />
                      <motion.div variants={itemVariants}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="#4b5b5d"
                          >
                            Privacy Policy URL
                          </Typography>
                          <Link
                            href={userData.whiteLabel.privacyPolicyURL}
                            target="_blank"
                            rel="noopener"
                            sx={{ textDecoration: "none", color: "#4b5b5d" }}
                          >
                            {userData.whiteLabel.privacyPolicyURL}
                          </Link>
                        </Box>
                      </motion.div>
                    </>
                  )}
                </InfoCard>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <InfoCard sx={{ position: "relative" }}>
                  <Title>Payment History</Title>
                  {(billingHistoryLoading || invoiceLoading) && (
                    <LoaderOverlay>
                      <CircularProgress sx={{ color: "#4b5b5d" }} />
                    </LoaderOverlay>
                  )}
                  {billingHistoryError && !billingHistoryLoading ? (
                    isNoBillingHistory ? (
                      <NoHistoryMessage>
                        No billing history available.
                      </NoHistoryMessage>
                    ) : (
                      <ErrorMessage>{billingHistoryError}</ErrorMessage>
                    )
                  ) : (
                    <Box
                      sx={{
                        overflowX: "auto",
                        "-webkit-overflow-scrolling": "touch",
                        scrollbarWidth: "thin",
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          background: "#f1f1f1",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "#4b5b5d",
                          borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          background: "#354142",
                        },
                      }}
                    >
                      <StyledTable size={isMobile ? "small" : "medium"}>
                        <TableHead sx={{ background: "#f5f0e7" }}>
                          <TableRow>
                            <StyledTableHeadCell>
                              Billing Date
                            </StyledTableHeadCell>
                            <StyledTableHeadCell>
                              Total Price
                            </StyledTableHeadCell>
                            <StyledTableHeadCell>Status</StyledTableHeadCell>
                            <StyledTableHeadCell>
                              Description
                            </StyledTableHeadCell>
                            <StyledTableHeadCell>Invoice</StyledTableHeadCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {billingHistory && billingHistory.length > 0 ? (
                            billingHistory.map((item) => (
                              <StyledTableRow key={item.billingHistoryId}>
                                <StyledTableCell>
                                  {item.paidAt
                                    ? format(
                                        new Date(item.paidAt),
                                        "MM-dd-yyyy"
                                      )
                                    : format(
                                        new Date(item.createdAt),
                                        "MM-dd-yyyy"
                                      )}
                                </StyledTableCell>
                                <StyledTableCell>
                                  ${item.amount}
                                </StyledTableCell>
                                <StyledTableCell>
                                  {item.status.toUpperCase()}
                                </StyledTableCell>
                                <StyledTableCell>
                                  <Tooltip title={item.description} arrow>
                                    <span>
                                      {truncateDescription(item.description)}
                                    </span>
                                  </Tooltip>
                                </StyledTableCell>
                                <StyledTableCell>
                                  <IconButton
                                    color="inherit"
                                    size="small"
                                    onClick={() =>
                                      handleDownloadInvoice(
                                        item.stripeInvoiceId,
                                        item.status
                                      )
                                    }
                                    disabled={
                                      billingHistoryLoading ||
                                      invoiceLoading ||
                                      downloadingInvoiceId ===
                                        item.billingHistoryId
                                    }
                                  >
                                    {downloadingInvoiceId ===
                                    item.billingHistoryId ? (
                                      <CircularProgress
                                        size={20}
                                        sx={{ color: "#4b5b5d" }}
                                      />
                                    ) : (
                                      <DownloadIcon />
                                    )}
                                  </IconButton>
                                </StyledTableCell>
                              </StyledTableRow>
                            ))
                          ) : (
                            <StyledTableRow>
                              <StyledTableCell colSpan={5} align="center">
                                No billing history available
                              </StyledTableCell>
                            </StyledTableRow>
                          )}
                        </TableBody>
                      </StyledTable>
                    </Box>
                  )}
                </InfoCard>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <InfoCard>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="#4b5b5d"
                      display="flex"
                      alignItems="center"
                    >
                      <DownloadIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                      Reports
                    </Typography>
                  </Box>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{
                      "& .MuiTab-root": {
                        color: "#4b5b5d",
                        fontWeight: 500,
                      },
                      "& .MuiTab-root.Mui-selected": {
                        color: "#455354",
                        fontWeight: 700,
                      },
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#455354",
                      },
                    }}
                  >
                    <Tab label="Site Estimator Reports" />
                    <Tab label="Fleet Estimator Reports" />
                    <Tab label="Embedded Reports" />
                  </Tabs>

                  {/* Site Estimator Reports */}
                  <TabPanel value={tabValue} index={0}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 1.5,
                      }}
                    >
                      <ExportButton
                        variant="contained"
                        disabled={
                          selectedGeneratedReports.length === 0 ||
                          reportsLoading ||
                          downloadingPdf !== null
                        }
                        onClick={() => handleDownloadMultiplePdfs("generated")}
                      >
                        {downloadingPdf === "multiple" ? (
                          <CircularProgress size={20} sx={{ color: "#fff" }} />
                        ) : (
                          "Export Report"
                        )}
                      </ExportButton>
                    </Box>
                    <Box
                      sx={{
                        overflowX: "auto",
                        "-webkit-overflow-scrolling": "touch",
                        scrollbarWidth: "thin",
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          background: "#f1f1f1",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "#4b5b5d",
                          borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          background: "#354142",
                        },
                      }}
                    >
                      {reportsLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 3,
                          }}
                        >
                          <CircularProgress
                            size={25}
                            thickness={2}
                            sx={{ color: "#455354" }}
                          />
                        </Box>
                      ) : reportsError ? (
                        <Typography
                          color="error"
                          variant="body1"
                          align="center"
                          sx={{ py: 3 }}
                        >
                          {reportsError}
                        </Typography>
                      ) : (
                        <StyledTable size={isMobile ? "small" : "medium"}>
                          <TableHead sx={{ background: "#f5f0e7" }}>
                            <TableRow>
                              <StyledTableHeadCell padding="checkbox">
                                <Checkbox
                                  sx={{
                                    color: "#4b5b5d",
                                    "&.Mui-checked": {
                                      color: "#4b5b5d",
                                    },
                                  }}
                                  size="small"
                                  indeterminate={
                                    selectedGeneratedReports.length > 0 &&
                                    selectedGeneratedReports.length <
                                      generatedReports.length
                                  }
                                  checked={
                                    generatedReports.length > 0 &&
                                    selectedGeneratedReports.length ===
                                      generatedReports.length
                                  }
                                  onChange={handleSelectAllGenerated}
                                  disabled={reportsLoading}
                                />
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>
                                Report Name
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>
                                Net Profit
                              </StyledTableHeadCell>
                              {!isSmallScreen && (
                                <StyledTableHeadCell>
                                  Generated On
                                </StyledTableHeadCell>
                              )}
                              <StyledTableHeadCell>
                                Download
                              </StyledTableHeadCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {generatedReports.length === 0 ? (
                              <TableRow>
                                <StyledTableCell
                                  colSpan={isSmallScreen ? 3 : 4}
                                  align="center"
                                  sx={{ py: 3 }}
                                >
                                  <Typography
                                    variant="h6"
                                    color="#4b5b5d"
                                    fontWeight={500}
                                  >
                                    No reports available
                                  </Typography>
                                </StyledTableCell>
                              </TableRow>
                            ) : (
                              generatedReports.map((report) => {
                                const isItemSelected = isGeneratedSelected(
                                  report._id
                                );
                                return (
                                  <TableRow
                                    key={report._id}
                                    selected={isItemSelected}
                                    sx={{
                                      "&:nth-of-type(odd)": {
                                        backgroundColor: "#f8f8f8",
                                      },
                                      "&:hover": {
                                        backgroundColor: "#e0e0e0",
                                      },
                                    }}
                                  >
                                    <StyledTableCell padding="checkbox">
                                      <Checkbox
                                        size="small"
                                        checked={isItemSelected}
                                        onChange={(event) =>
                                          handleSelectGenerated(
                                            event,
                                            report._id
                                          )
                                        }
                                        sx={{
                                          color: "#4b5b5d",
                                          "&.Mui-checked": {
                                            color: "#4b5b5d",
                                          },
                                        }}
                                        disabled={reportsLoading}
                                      />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      <Typography
                                        fontWeight={500}
                                        fontSize={
                                          isSmallScreen ? "0.75rem" : "0.8rem"
                                        }
                                      >
                                        {report.reportName}
                                      </Typography>
                                      {isSmallScreen && (
                                        <Typography
                                          variant="caption"
                                          color="#4b5b5d"
                                        >
                                          {formatDate(report.createdAt)}
                                        </Typography>
                                      )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      <Typography
                                        fontWeight={500}
                                        fontSize={
                                          isSmallScreen ? "0.75rem" : "0.8rem"
                                        }
                                      >
                                        ${report.netProfit.toLocaleString()}
                                      </Typography>
                                    </StyledTableCell>
                                    {!isSmallScreen && (
                                      <StyledTableCell>
                                        {formatDate(report.createdAt)}
                                      </StyledTableCell>
                                    )}
                                    <StyledTableCell>
                                      <Tooltip title="Download PDF">
                                        <DownloadButton
                                          onClick={() =>
                                            handleDownloadPdf(report)
                                          }
                                          disabled={
                                            reportsLoading ||
                                            downloadingPdf === report._id
                                          }
                                        >
                                          {downloadingPdf === report._id ? (
                                            <CircularProgress
                                              size={16}
                                              sx={{ color: "#4b5b5d" }}
                                            />
                                          ) : (
                                            <PictureAsPdfIcon
                                              fontSize={
                                                isSmallScreen
                                                  ? "small"
                                                  : "medium"
                                              }
                                            />
                                          )}
                                        </DownloadButton>
                                      </Tooltip>
                                    </StyledTableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </StyledTable>
                      )}
                    </Box>
                  </TabPanel>

                  {/* Fleet Estimator Reports */}
                  <TabPanel value={tabValue} index={1}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 1.5,
                      }}
                    >
                      <ExportButton
                        variant="contained"
                        disabled={
                          selectedFleetReports.length === 0 ||
                          fleetReportsLoading ||
                          downloadingPdf !== null
                        }
                        onClick={() => handleDownloadMultipleFleetPdfs()}
                      >
                        {downloadingPdf === "multiple" ? (
                          <CircularProgress size={20} sx={{ color: "#fff" }} />
                        ) : (
                          "Export Report"
                        )}
                      </ExportButton>
                    </Box>
                    <Box
                      sx={{
                        overflowX: "auto",
                        "-webkit-overflow-scrolling": "touch",
                        scrollbarWidth: "thin",
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          background: "#f1f1f1",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "#4b5b5d",
                          borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          background: "#354142",
                        },
                      }}
                    >
                      {fleetReportsLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 3,
                          }}
                        >
                          <CircularProgress
                            size={25}
                            thickness={2}
                            sx={{ color: "#455354" }}
                          />
                        </Box>
                      ) : fleetReportsError ? (
                        <Typography
                          color="error"
                          variant="body1"
                          align="center"
                          sx={{ py: 3 }}
                        >
                          {fleetReportsError}
                        </Typography>
                      ) : (
                        <StyledTable size={isMobile ? "small" : "medium"}>
                          <TableHead sx={{ background: "#f5f0e7" }}>
                            <TableRow>
                              <StyledTableHeadCell padding="checkbox">
                                <Checkbox
                                  sx={{
                                    color: "#4b5b5d",
                                    "&.Mui-checked": {
                                      color: "#4b5b5d",
                                    },
                                  }}
                                  size="small"
                                  indeterminate={
                                    selectedFleetReports.length > 0 &&
                                    selectedFleetReports.length <
                                      fleetReports.length
                                  }
                                  checked={
                                    fleetReports.length > 0 &&
                                    selectedFleetReports.length ===
                                      fleetReports.length
                                  }
                                  onChange={handleSelectAllFleet}
                                  disabled={fleetReportsLoading}
                                />
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>
                                Report Name
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>
                                Net Savings
                              </StyledTableHeadCell>
                              {!isSmallScreen && (
                                <StyledTableHeadCell>
                                  Generated On
                                </StyledTableHeadCell>
                              )}
                              <StyledTableHeadCell>
                                Download
                              </StyledTableHeadCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {fleetReports.length === 0 ? (
                              <TableRow>
                                <StyledTableCell
                                  colSpan={isSmallScreen ? 3 : 4}
                                  align="center"
                                  sx={{ py: 3 }}
                                >
                                  <Typography
                                    variant="h6"
                                    color="#4b5b5d"
                                    fontWeight={500}
                                  >
                                    No fleet reports available
                                  </Typography>
                                </StyledTableCell>
                              </TableRow>
                            ) : (
                              fleetReports.map((report) => {
                                const isItemSelected = isFleetSelected(
                                  report._id
                                );
                                return (
                                  <TableRow
                                    key={report._id}
                                    selected={isItemSelected}
                                    sx={{
                                      "&:nth-of-type(odd)": {
                                        backgroundColor: "#f8f8f8",
                                      },
                                      "&:hover": {
                                        backgroundColor: "#e0e0e0",
                                      },
                                    }}
                                  >
                                    <StyledTableCell padding="checkbox">
                                      <Checkbox
                                        size="small"
                                        checked={isItemSelected}
                                        onChange={(event) =>
                                          handleSelectFleet(event, report._id)
                                        }
                                        sx={{
                                          color: "#4b5b5d",
                                          "&.Mui-checked": {
                                            color: "#4b5b5d",
                                          },
                                        }}
                                        disabled={fleetReportsLoading}
                                      />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      <Typography
                                        fontWeight={500}
                                        fontSize={
                                          isSmallScreen ? "0.75rem" : "0.8rem"
                                        }
                                      >
                                        {report.reportName}
                                      </Typography>
                                      {isSmallScreen && (
                                        <Typography
                                          variant="caption"
                                          color="#4b5b5d"
                                        >
                                          {formatDate(report.createdAt)}
                                        </Typography>
                                      )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      <Typography
                                        fontWeight={500}
                                        fontSize={
                                          isSmallScreen ? "0.75rem" : "0.8rem"
                                        }
                                      >
                                        $
                                        {report.netSavings?.toLocaleString() ||
                                          "0"}
                                      </Typography>
                                    </StyledTableCell>
                                    {!isSmallScreen && (
                                      <StyledTableCell>
                                        {formatDate(report.createdAt)}
                                      </StyledTableCell>
                                    )}
                                    <StyledTableCell>
                                      <Tooltip title="Download PDF">
                                        <DownloadButton
                                          onClick={() =>
                                            handleDownloadFleetPdf(report)
                                          }
                                          disabled={
                                            fleetReportsLoading ||
                                            downloadingPdf === report._id
                                          }
                                        >
                                          {downloadingPdf === report._id ? (
                                            <CircularProgress
                                              size={16}
                                              sx={{ color: "#4b5b5d" }}
                                            />
                                          ) : (
                                            <PictureAsPdfIcon
                                              fontSize={
                                                isSmallScreen
                                                  ? "small"
                                                  : "medium"
                                              }
                                            />
                                          )}
                                        </DownloadButton>
                                      </Tooltip>
                                    </StyledTableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </StyledTable>
                      )}
                    </Box>
                  </TabPanel>

                  {/* Embedded Reports */}
                  <TabPanel value={tabValue} index={2}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 1.5,
                      }}
                    >
                      <ExportButton
                        variant="contained"
                        disabled={
                          selectedEmbeddedReports.length === 0 ||
                          embeddedReportsLoading ||
                          downloadingPdf !== null
                        }
                        onClick={() => handleDownloadMultiplePdfs("embedded")}
                      >
                        {downloadingPdf === "multiple" ? (
                          <CircularProgress size={20} sx={{ color: "#fff" }} />
                        ) : (
                          "Export Report"
                        )}
                      </ExportButton>
                    </Box>
                    <Box
                      sx={{
                        overflowX: "auto",
                        "-webkit-overflow-scrolling": "touch",
                        scrollbarWidth: "thin",
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          background: "#f1f1f1",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "#4b5b5d",
                          borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          background: "#354142",
                        },
                      }}
                    >
                      {embeddedReportsLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 3,
                          }}
                        >
                          <CircularProgress
                            size={25}
                            thickness={2}
                            sx={{ color: "#455354" }}
                          />
                        </Box>
                      ) : embeddedReportsError ? (
                        <Typography
                          color="error"
                          variant="body1"
                          align="center"
                          sx={{ py: 3 }}
                        >
                          {embeddedReportsError}
                        </Typography>
                      ) : embeddedReports.length > 0 ? (
                        <StyledTable size={isMobile ? "small" : "medium"}>
                          <TableHead sx={{ background: "#f5f0e7" }}>
                            <TableRow>
                              <StyledTableHeadCell padding="checkbox">
                                <Checkbox
                                  sx={{
                                    color: "#4b5b5d",
                                    "&.Mui-checked": {
                                      color: "#4b5b5d",
                                    },
                                  }}
                                  size="small"
                                  indeterminate={
                                    selectedEmbeddedReports.length > 0 &&
                                    selectedEmbeddedReports.length <
                                      embeddedReports.length
                                  }
                                  checked={
                                    embeddedReports.length > 0 &&
                                    selectedEmbeddedReports.length ===
                                      embeddedReports.length
                                  }
                                  onChange={handleSelectAllEmbedded}
                                  disabled={embeddedReportsLoading}
                                />
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>
                                Full Name
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>Email</StyledTableHeadCell>
                              <StyledTableHeadCell>
                                Report Name
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>
                                Net Profit
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>
                                Location
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>
                                Generated On
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>Action</StyledTableHeadCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {embeddedReports.map((report) => {
                              const isItemSelected = isEmbeddedSelected(
                                report.estimator._id
                              );
                              return (
                                <TableRow
                                  key={report.estimator._id}
                                  selected={isItemSelected}
                                  sx={{
                                    "&:nth-of-type(odd)": {
                                      backgroundColor: "#f8f8f8",
                                    },
                                    "&:hover": {
                                      backgroundColor: "#e0e0e0",
                                    },
                                  }}
                                >
                                  <StyledTableCell padding="checkbox">
                                    <Checkbox
                                      size="small"
                                      checked={isItemSelected}
                                      onChange={(event) =>
                                        handleSelectEmbedded(
                                          event,
                                          report.estimator._id
                                        )
                                      }
                                      sx={{
                                        color: "#4b5b5d",
                                        "&.Mui-checked": {
                                          color: "#4b5b5d",
                                        },
                                      }}
                                      disabled={embeddedReportsLoading}
                                    />
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <Typography
                                      fontWeight={500}
                                      fontSize={
                                        isSmallScreen ? "0.75rem" : "0.8rem"
                                      }
                                    >
                                      {report.firstName} {report.lastName}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <Typography
                                      fontWeight={500}
                                      fontSize={
                                        isSmallScreen ? "0.75rem" : "0.8rem"
                                      }
                                    >
                                      {report.email}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <Typography
                                      fontWeight={500}
                                      fontSize={
                                        isSmallScreen ? "0.75rem" : "0.8rem"
                                      }
                                    >
                                      {report.estimator.reportName}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <Typography
                                      fontWeight={500}
                                      fontSize={
                                        isSmallScreen ? "0.75rem" : "0.8rem"
                                      }
                                    >
                                      $
                                      {report.estimator.netProfit.toLocaleString()}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <Typography
                                      fontWeight={500}
                                      fontSize={
                                        isSmallScreen ? "0.75rem" : "0.8rem"
                                      }
                                    >
                                      {report.estimator.location || "N/A"}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <Typography
                                      fontWeight={500}
                                      fontSize={
                                        isSmallScreen ? "0.75rem" : "0.8rem"
                                      }
                                    >
                                      {formatDate(report.estimator.createdAt)}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <motion.div
                                      variants={buttonVariants}
                                      initial="rest"
                                      whileHover="hover"
                                      whileTap="tap"
                                    >
                                      <Tooltip title="Download PDF">
                                        <DownloadButton
                                          onClick={() =>
                                            handleDownloadPdf(
                                              report.estimator,
                                              {
                                                firstName: report.firstName,
                                                lastName: report.lastName,
                                                email: report.email,
                                              }
                                            )
                                          }
                                          disabled={
                                            embeddedReportsLoading ||
                                            downloadingPdf ===
                                              report.estimator._id
                                          }
                                        >
                                          {downloadingPdf ===
                                          report.estimator._id ? (
                                            <CircularProgress
                                              size={16}
                                              sx={{ color: "#4b5b5d" }}
                                            />
                                          ) : (
                                            <PictureAsPdfIcon
                                              fontSize={
                                                isSmallScreen
                                                  ? "small"
                                                  : "medium"
                                              }
                                            />
                                          )}
                                        </DownloadButton>
                                      </Tooltip>
                                    </motion.div>
                                  </StyledTableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </StyledTable>
                      ) : (
                        <Typography
                          variant="h6"
                          color="#4b5b5d"
                          fontWeight={500}
                          align="center"
                          sx={{ py: 3 }}
                        >
                          No embedded reports available
                        </Typography>
                      )}
                    </Box>
                  </TabPanel>
                </InfoCard>
              </Grid>
            </Grid>
          </motion.div>
        ) : (
          <Typography variant="h6" color="#4b5b5d" textAlign="center">
            User not found
          </Typography>
        )}

        {tabValue === 0 && !reportsLoading && (
          <FooterContainer>
            <Typography
              variant="body2"
              color="#4b5b5d"
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Showing {(generatedPage - 1) * generatedPageSize + 1} to{" "}
              {Math.min(
                generatedPage * generatedPageSize,
                generatedTotalReports
              )}{" "}
              of {generatedTotalReports} entries
            </Typography>

            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="#4b5b5d">
                  Show:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={generatedPageSize}
                    onChange={handleGeneratedPageSizeChange}
                    sx={{
                      "& .MuiSelect-select": {
                        padding: "4px 8px",
                        fontSize: "0.8rem",
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="#4b5b5d">
                  entries
                </Typography>
              </Box>
            )}

            <Pagination
              count={generatedTotalPages}
              page={generatedPage}
              onChange={handleGeneratedPageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
                "& .MuiPaginationItem-root": {
                  color: "#4b5b5d",
                  "&.Mui-selected": {
                    backgroundColor: "#758d88",
                    color: "#f8f9fa",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#4b5b5d",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(75, 91, 93, 0.04)",
                  },
                },
              }}
            />
          </FooterContainer>
        )}

        {tabValue === 1 && !fleetReportsLoading && (
          <FooterContainer>
            <Typography
              variant="body2"
              color="#4b5b5d"
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Showing {(fleetPage - 1) * fleetPageSize + 1} to{" "}
              {Math.min(fleetPage * fleetPageSize, fleetTotalReports)} of{" "}
              {fleetTotalReports} entries
            </Typography>

            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="#4b5b5d">
                  Show:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={fleetPageSize}
                    onChange={handleFleetPageSizeChange}
                    sx={{
                      "& .MuiSelect-select": {
                        padding: "4px 8px",
                        fontSize: "0.8rem",
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="#4b5b5d">
                  entries
                </Typography>
              </Box>
            )}

            <Pagination
              count={fleetTotalPages}
              page={fleetPage}
              onChange={handleFleetPageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
                "& .MuiPaginationItem-root": {
                  color: "#4b5b5d",
                  "&.Mui-selected": {
                    backgroundColor: "#758d88",
                    color: "#f8f9fa",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#4b5b5d",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(75, 91, 93, 0.04)",
                  },
                },
              }}
            />
          </FooterContainer>
        )}

        {tabValue === 2 && !embeddedReportsLoading && (
          <FooterContainer>
            <Typography
              variant="body2"
              color="#4b5b5d"
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Showing {(embeddedPage - 1) * embeddedPageSize + 1} to{" "}
              {Math.min(embeddedPage * embeddedPageSize, embeddedTotalReports)}{" "}
              of {embeddedTotalReports} entries
            </Typography>

            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="#4b5b5d">
                  Show:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={embeddedPageSize}
                    onChange={handleEmbeddedPageSizeChange}
                    sx={{
                      "& .MuiSelect-select": {
                        padding: "4px 8px",
                        fontSize: "0.8rem",
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="#4b5b5d">
                  entries
                </Typography>
              </Box>
            )}

            <Pagination
              count={embeddedTotalPages}
              page={embeddedPage}
              onChange={handleEmbeddedPageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
                "& .MuiPaginationItem-root": {
                  color: "#4b5b5d",
                  "&.Mui-selected": {
                    backgroundColor: "#758d88",
                    color: "#f8f9fa",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#4b5b5d",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(75, 91, 93, 0.04)",
                  },
                },
              }}
            />
          </FooterContainer>
        )}
      </motion.div>
    </Container>
  );
};

export default ViewUser;
